import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { isMobile, isTablet } from "react-device-detect";
import { color, fontWeight } from "../../theme/tokens";
import {
  logWebTrialEvent,
  type WebTrialParams,
} from "../../utils/getAppAnalytics";
import WebTrialConfirm from "./WebTrialConfirm";

/**
 * #1261 (Free Feed, Increment 3, Slice 2): the "No smartphone or tablet?"
 * card, for someone on a computer who can start a trial. Board FF-1260-C,
 * screen 8, placed by option B's rule: computers only (Ellaie, 3 Nov, ask 82:
 * "if we know its a phone, no need to focus on alternatives"). Phones and
 * tablets get #1259's get-the-app card and nothing else.
 *
 * **SUBSCRIPTIONS / MEMBERSHIP AND PAYMENTS ESCALATION CLASS** (what a
 * non-member is offered). The card itself starts nothing: its button opens
 * the confirm step (`WebTrialConfirm`), which starts the trial.
 *
 * Shown on My Account under #1259's card, and on sign-up's last screen under
 * the code to scan. Who sees it: a computer (the device split copied from
 * `GetAppCard.tsx`), a status that can start a trial, and the server listing a
 * trial product for this account (as `SignUpStepGetApp.tsx` asks it).
 *
 * Styling from the board, which draws My Account's membership panel
 * (`Overview.js` `topMembershipContainerSx`, #e2e2e2) with the blue
 * information edge (`upgradeMembershipContainerSx`, #0f98d4). Every colour is
 * named (app-web CLAUDE.md).
 */

/** The statuses that can start a trial (#1261 done-when 1 and 6): a free
 *  account and new-trial-eligible. */
export const WEB_TRIAL_STATUSES = ["standard-incomplete", "new-trial-eligible"];

// Exact words from the ticket (done-when 1) and board FF-1260-C, screen 8.
export const WEB_TRIAL_HEADING = "No smartphone or tablet?";
const LINE =
  "Start your 3-day free trial here and watch on our website instead.";
const BUTTON = "START MY TRIAL ON THE WEBSITE";

// Board: #e2e2e2 ground, 10px blue edge, 32px padding, 16px gap, and 24px
// above it (the gap under #1259's card on My Account).
const cardSx: SxProps<Theme> = {
  marginTop: "24px",
  backgroundColor: "#e2e2e2",
  borderLeft: "10px solid #0f98d4",
  padding: "32px",
  boxSizing: "border-box",
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: "16px",
  textAlign: "left",
};

const wordsSx: SxProps<Theme> = {
  flex: "1 1 300px",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: "6px",
};

// Board: 24px, weight 500.
const headingSx: SxProps<Theme> = {
  color: color.textPrimary,
  fontSize: "1.5rem",
  lineHeight: 1.2,
  fontWeight: fontWeight.medium,
  margin: 0,
};

const lineSx: SxProps<Theme> = {
  color: color.textPrimary,
  lineHeight: 1.45,
};

// Board: outlined pill, 2px #171717, 44px high, 14px weight 500, 24px either
// side. The theme gives the 2px border and the 25px corners.
const buttonSx: SxProps<Theme> = {
  flex: "0 0 auto",
  minHeight: "44px",
  margin: 0,
  padding: "0 24px",
  fontSize: "0.875rem",
  fontWeight: fontWeight.medium,
  letterSpacing: "0.5px",
  color: color.ffNavy,
  borderColor: color.ffNavy,
  "&:hover": { color: color.ffNavy, borderColor: color.ffNavy },
};

type WebTrialCardProps = {
  /** The signed-in member's status. */
  memberStatusId?: string;
  /** Whether the server lists a trial product for this account. */
  trialOffered: boolean;
  /** Where the card is: My Account or sign-up's last screen. */
  surface: WebTrialParams["surface"];
};

const WebTrialCard = ({
  memberStatusId,
  trialOffered,
  surface,
}: WebTrialCardProps) => {
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (
    isMobile ||
    isTablet ||
    !trialOffered ||
    !memberStatusId ||
    !WEB_TRIAL_STATUSES.includes(memberStatusId)
  ) {
    return null;
  }

  const open = () => {
    logWebTrialEvent("web_trial_card_click", {
      member_status: memberStatusId,
      surface,
    });
    setConfirmOpen(true);
  };

  return (
    <>
      <Box component="section" aria-label={WEB_TRIAL_HEADING} sx={cardSx}>
        <Box sx={wordsSx}>
          <Typography variant="h2" sx={headingSx}>
            {WEB_TRIAL_HEADING}
          </Typography>
          <Typography variant="body1" sx={lineSx}>
            {LINE}
          </Typography>
        </Box>
        <Button variant="outlined" onClick={open} sx={buttonSx}>
          {BUTTON}
        </Button>
      </Box>
      <WebTrialConfirm
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        surface={surface}
        memberStatusId={memberStatusId}
      />
    </>
  );
};

export default WebTrialCard;
